// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — « Mes espaces » : organisations (Œuvres sociales, coopérative…) auxquelles la
// personne a accès, soit par un mandat en cours, soit par une clé transverse attribuée explicitement
// (administration des organisations ou des mandats, contrôle). Données : /socle/moi/espaces,
// /socle/organisations (paginé), /socle/a-traiter.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, Inbox, Plus, ScanQrCode } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { EnTetePage, Bouton, BandeauInfo, EtatVide, Squelette } from '../../design/composants';
import { useDs, toArray, libelle, TYPES_ORGANISATION, ETATS_ORGANISATION, NATURES_MANDAT } from './api';
import { useEspaces } from './espaces';
import { useLecture, ErreurServeur, Etat, PaginationDs } from './commun';
import { FenetreNouvelleOrganisation } from './FenetresStructure';

const CLES_TRANSVERSES = {
  ds_organisations_administrer: 'administration des organisations',
  ds_mandats_administrer: 'administration des mandats',
  ds_controle_lecture: 'contrôle (lecture seule)',
};

const fonctionsTenues = (org) => toArray(org?.mandats).map((m) => (m.nature && m.nature !== 'TITULAIRE'
  ? `${m.fonction_libelle} (${libelle(NATURES_MANDAT, m.nature).toLowerCase()})` : m.fonction_libelle)).join(', ');

function ATraiter({ elements, organisations }) {
  if (!elements.length) return null;
  const nomOrg = (id) => organisations.find((o) => o.id === id)?.libelle || 'Organisation';
  return (
    <BandeauInfo ton="info" titre="À traiter">
      <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none' }}>
        {elements.map((a) => (
          <li key={`${a.organisation_id}-${a.libelle}`} style={{ padding: '2px 0' }}>
            <Link className="rh-lien" to={a.lien}>{a.libelle}{a.nombre > 1 ? ` (${a.nombre})` : ''}</Link>
            <span style={{ color: 'var(--sg-texte-3)' }}> — {nomOrg(a.organisation_id)}</span>
          </li>
        ))}
      </ul>
    </BandeauInfo>
  );
}

function LigneOrganisation({ o, espace, onOuvrir }) {
  const fonctions = fonctionsTenues(espace);
  return (
    <tr className="rh-ligne" tabIndex={0} onClick={onOuvrir} onKeyDown={(e) => { if (e.key === 'Enter') onOuvrir(); }}>
      <td>
        <span className="rh-cellule-principale">{o.libelle}</span>
        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{o.code}{o.donnees_demo ? ' · données de démonstration' : ''}</span>
      </td>
      <td>{libelle(TYPES_ORGANISATION, o.type)}</td>
      <td><Etat table={ETATS_ORGANISATION} code={o.etat} /></td>
      <td>{fonctions || <span className="rh-cellule-secondaire">Accès transverse</span>}</td>
      <td style={{ width: 32, color: 'var(--sg-texte-4)' }}><ChevronRight size={16} aria-hidden="true" /></td>
    </tr>
  );
}

export default function EspacesPage() {
  const ds = useDs();
  const naviguer = useNavigate();
  const { hasPermission } = useAuth();
  const { espaces, erreur: erreurEspaces, recharger: rechargerEspaces } = useEspaces();
  const [page, setPage] = useState(1);
  const [creation, setCreation] = useState(false);
  const liste = useLecture(() => ds.organisations(page, 25), [ds, page]);
  const aTraiter = useLecture(() => ds.aTraiter(), [ds]);

  const transverses = toArray(espaces?.cles_transverses).filter((k) => CLES_TRANSVERSES[k]);
  const administre = transverses.includes('ds_organisations_administrer');
  const organisations = toArray(liste.donnees);
  const espaceDe = (id) => toArray(espaces?.organisations).find((x) => x.organisation_id === id);

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Dispositifs sociaux' }]} titre="Dispositifs sociaux"
          sousTitre="Les organisations sociales (Œuvres sociales, coopérative…) dont vous tenez un mandat ou dont vous avez la charge, avec leurs instances et leurs dispositifs."
          actions={(administre || hasPermission('controler_cartes_employes')) ? (
            <>
              {hasPermission('controler_cartes_employes') && <Bouton Icone={ScanQrCode} onClick={() => naviguer('/dispositifs/controle-carte')}>Contrôler une carte</Bouton>}
              {administre && <Bouton variante="primaire" Icone={Plus} onClick={() => setCreation(true)}>Nouvelle organisation</Bouton>}
            </>
          ) : null} />

        {erreurEspaces && <ErreurServeur erreur={erreurEspaces} />}
        {transverses.length > 0 && (
          <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>
            Vous voyez toutes les organisations au titre de : {transverses.map((k) => CLES_TRANSVERSES[k]).join(', ')}.
          </p>
        )}
        <ATraiter elements={toArray(aTraiter.donnees)} organisations={organisations} />

        <section className="rh-carte" style={{ padding: 0 }}>
          {liste.erreur && <div style={{ padding: 16 }}><ErreurServeur erreur={liste.erreur} action={<Bouton petit onClick={liste.recharger}>Réessayer</Bouton>} /></div>}
          {!liste.donnees && !liste.erreur && <div style={{ padding: 16 }}><Squelette largeur="60%" /><Squelette largeur="40%" style={{ marginTop: 10 }} /></div>}
          {liste.donnees && !organisations.length && (
            <EtatVide Icone={administre ? Building2 : Inbox} titre="Aucune organisation"
              texte={administre
                ? 'Aucune organisation n\'a encore été créée. Créez la première ; un autre administrateur la mettra en service (double regard).'
                : 'Vous ne tenez aucun mandat en cours dans une organisation sociale. Les mandats sont enregistrés sur pièce (PV, décision) par l\'administrateur des mandats, puis confirmés par une seconde personne.'} />
          )}
          {organisations.length > 0 && (
            <>
              <div className="rh-conteneur-tableau">
                <table className="rh-tableau">
                  <thead><tr><th>Organisation</th><th>Type</th><th>État</th><th>Mes fonctions</th><th aria-label="Ouvrir" /></tr></thead>
                  <tbody>
                    {organisations.map((o) => (
                      <LigneOrganisation key={o.id} o={o} espace={espaceDe(o.id)} onOuvrir={() => naviguer(`/dispositifs/${o.id}/instances`)} />
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationDs donnees={liste.donnees} onPage={setPage} />
            </>
          )}
        </section>

        {creation && (
          <FenetreNouvelleOrganisation onFermer={() => setCreation(false)}
            onCree={(o) => { setCreation(false); rechargerEspaces(); naviguer(`/dispositifs/${o.id}/instances`); }} />
        )}
      </div>
    </Layout>
  );
}
